import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { EditorState, RichUtils, Modifier } from "draft-js";
import Editor from "draft-js-plugins-editor";
import { stateToHTML } from "draft-js-export-html";
import { stateFromHTML } from "draft-js-import-html";
import { InlineStyleControls, BlockStyleControls } from "./Controls";
import { createVariablePlugin, VariablePickerToolbar } from "./Variables";

const ALIGN_VALUES = ['left', 'right', 'center', 'justify'];

const HEADING_SIZE_PT = {
  'header-one': 20, 'header-two': 16, 'header-three': 14,
  'header-four': 12, 'header-five': 11, 'header-six': 10,
};

const escapeHtml = (s) => s
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const alignStyle = (block) => {
  const align = block.getData() && block.getData().get('textAlign');
  return align && ALIGN_VALUES.includes(align) ? `text-align:${align};` : '';
};

const renderHeading = (block, pt) => {
  const text = escapeHtml(block.getText());
  const align = alignStyle(block);
  const styleAttr = align ? ` style="${align}"` : '';
  return `<p${styleAttr}><b style="font-size:${pt}pt;">${text || '&nbsp;'}</b></p>`;
};

const setAlignment = (editorState, align) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(selection.getStartKey());
  const current = block.getData().get('textAlign');
  const nextData = current === align
    ? block.getData().delete('textAlign')
    : block.getData().set('textAlign', align);
  const newContent = Modifier.setBlockData(contentState, selection, nextData);
  return EditorState.push(editorState, newContent, 'change-block-data');
};

const exportOptions = {
  blockStyleFn: (block) => {
    const align = block.getData() && block.getData().get('textAlign');
    if (align && ALIGN_VALUES.includes(align)) {
      return { attributes: { style: `text-align: ${align};` } };
    }
    return null;
  },
  blockRenderers: {
    unstyled: (block) => {
      if (!block.getText().trim()) {
        const align = alignStyle(block);
        const styleAttr = align ? ` style="${align}"` : '';
        return `<p${styleAttr}>&nbsp;</p>`;
      }
      return null; // default handles non-empty + applies blockStyleFn
    },
    'header-one': (block) => renderHeading(block, HEADING_SIZE_PT['header-one']),
    'header-two': (block) => renderHeading(block, HEADING_SIZE_PT['header-two']),
    'header-three': (block) => renderHeading(block, HEADING_SIZE_PT['header-three']),
    'header-four': (block) => renderHeading(block, HEADING_SIZE_PT['header-four']),
    'header-five': (block) => renderHeading(block, HEADING_SIZE_PT['header-five']),
    'header-six': (block) => renderHeading(block, HEADING_SIZE_PT['header-six']),
    blockquote: (block) => {
      const text = escapeHtml(block.getText());
      const align = alignStyle(block);
      return `<p style="margin-left:24pt;font-style:italic;color:#555555;${align}">${text || '&nbsp;'}</p>`;
    },
  },
  inlineStyles: {
    BOLD: { element: 'b' },
    ITALIC: { element: 'i' },
    UNDERLINE: { element: 'u' },
  },
};

const importOptions = {
  customBlockFn: (element) => {
    const result = {};

    if (element.style && element.style.textAlign) {
      const align = element.style.textAlign.toLowerCase();
      if (ALIGN_VALUES.includes(align)) {
        result.data = { textAlign: align };
      }
    }

    if (element.tagName === 'P' && element.children.length === 1
        && element.firstChild.tagName === 'B'
        && element.firstChild.style && element.firstChild.style.fontSize) {
      const pt = parseInt(element.firstChild.style.fontSize, 10);
      const headingEntry = Object.entries(HEADING_SIZE_PT).find(([_, v]) => v === pt);
      if (headingEntry) result.type = headingEntry[0];
    }

    if (element.tagName === 'P' && element.style
        && element.style.marginLeft === '24pt'
        && element.style.fontStyle === 'italic') {
      result.type = 'blockquote';
    }

    return Object.keys(result).length > 0 ? result : null;
  },
};

const blockStyleFn = (block) => {
  const align = block.getData() && block.getData().get('textAlign');
  return align ? `align-${align}` : '';
};

const RichTextHtmlEditor = ({ value, onChange, disabled, rows = 8, variables }) => {
  const [editor, setEditor] = useState(null);

  const [editorState, setEditorState] = useState(() => {
    if (value) {
      const contentState = stateFromHTML(value, importOptions);
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });

  const variablePlugin = useMemo(
    () => (variables ? createVariablePlugin(variables) : null),
    [variables]
  );
  const plugins = useMemo(
    () => (variablePlugin ? [variablePlugin] : []),
    [variablePlugin]
  );

  useEffect(() => {
    if (value !== stateToHTML(editorState.getCurrentContent(), exportOptions)) {
      const contentState = stateFromHTML(value || "", importOptions);
      setEditorState(EditorState.createWithContent(contentState));
    }
    // eslint-disable-next-line
  }, [value]);

  const handleChange = (state) => {
    setEditorState(state);
    const html = stateToHTML(state.getCurrentContent(), exportOptions);
    if (html !== value) {
      onChange && onChange(html);
    }
  };

  const handleInlineToggle = (style) => {
    const newState = RichUtils.toggleInlineStyle(editorState, style);
    setEditorState(newState);
  };

  const handleBlockToggle = (blockType) => {
    const newState = RichUtils.toggleBlockType(editorState, blockType);
    setEditorState(newState);
  };

  const handleAlign = (align) => {
    setEditorState(setAlignment(editorState, align));
  };

  const focus = () => {
    editor.focus();
  };

  const lineHeight = 22;
  const minHeight = rows * lineHeight;
  return (
    <div className="cro-rich-text-editor" style={{ minHeight }} onClick={focus}>
      {!disabled &&
      <div className="controls-wrapper">
        <InlineStyleControls
          editorState={editorState}
          onToggle={handleInlineToggle}
        />
        <BlockStyleControls
          editorState={editorState}
          onToggle={handleBlockToggle}
          onAlign={handleAlign}
        />
        {variables && (
          <VariablePickerToolbar
            variableDefs={variables}
            getEditorState={() => editorState}
            setEditorState={setEditorState}
          />
        )}
      </div>}
      <Editor
        ref={(node) => setEditor(node)}
        editorState={editorState}
        onChange={handleChange}
        readOnly={!!disabled}
        plugins={plugins}
        blockStyleFn={blockStyleFn}
      />
    </div>
  );
};

RichTextHtmlEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  rows: PropTypes.number,
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
};

export default RichTextHtmlEditor;
